import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import mixpanel from "mixpanel-browser";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import MetricSummaryTile from "../../../../ui-components/metric-summary-tile/metricSummaryTile";
import XGSSelect, { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import XGSDateRangePickerWithPresets from "../../../../ui-components/xgs-date/xgs-date-range-picker/xgsDateRangePickerWithPresets";
import {
  getDateRangeForPreset,
  toUiDateString,
} from "../../../../ui-components/xgs-date/xgs-date-range-picker/dateRangePresets";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import {
  AnalyticsGranularity,
  AnalyticsOverview,
  FactoredInvoiceStatus,
  InvoiceListView,
} from "../../../../app/data/invoice-factoring/models";
import { FACTORING_QUERY_OPTIONS, GRANULARITY_LABELS, formatCents, formatDateTime, pluralize } from "../constants";
import { useFactoringContext } from "../common/useFactoringContext";
import FactoringNav from "../common/factoringNav";
import { formatDuration, formatRate, toApiRange } from "../utils/metrics";
import { DocumentGapChart, PayorTrendChart, SubmissionTrendChart } from "./overviewCharts";
import {
  BatchMonitorTable,
  DuplicatePreventionTable,
  IssuesTable,
  PayorsTable,
  PipelineTable,
  RetryHealthTable,
} from "./overviewTables";
import MetricToggle, { TrendMetric } from "./metricToggle";
import "../invoiceFactoring.scss";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const granularityOptions: XGSSelectOption[] = (Object.keys(GRANULARITY_LABELS) as AnalyticsGranularity[]).map(
  (granularity) => ({ value: granularity, label: GRANULARITY_LABELS[granularity] })
);

const selectMenuStyles = {
  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 1100 }),
};

const getDefaultRange = () => {
  const { start, end } = getDateRangeForPreset("LAST_30_DAYS");
  return { start: toUiDateString(start), end: toUiDateString(end) };
};

const OverviewCard: React.FC<{ title: string; description?: string; wide?: boolean; actions?: React.ReactNode }> = ({
  title,
  description,
  wide,
  actions,
  children,
}) => (
  <section className={`xgs-invoice-factoring__card${wide ? " xgs-invoice-factoring__card--wide" : ""}`}>
    <div className="xgs-invoice-factoring__card-head">
      <div>
        <div className="xgs-invoice-factoring__card-title">{title}</div>
        {description && <div className="xgs-invoice-factoring__muted">{description}</div>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

const TrendTotals: React.FC<{ overview: AnalyticsOverview; metric: TrendMetric }> = ({ overview, metric }) => {
  const total = (pick: (point: AnalyticsOverview["trend"][number]) => number) =>
    overview.trend.reduce((sum, point) => sum + pick(point), 0);
  const isValue = metric === "value";
  return (
    <div className="xgs-invoice-factoring__stats">
      <div>
        <span>Invoices tracked</span>
        <strong>{isValue ? formatCents(total((p) => p.trackedValueCents)) : total((p) => p.tracked)}</strong>
      </div>
      <div>
        <span>Submissions completed</span>
        <strong>{isValue ? formatCents(total((p) => p.submittedValueCents)) : total((p) => p.submitted)}</strong>
      </div>
    </div>
  );
};

interface InvoicesFilter {
  status?: FactoredInvoiceStatus;
  view?: InvoiceListView;
}

const OverviewTiles: React.FC<{
  overview?: AnalyticsOverview;
  isLoading: boolean;
  onOpen: (filter: InvoicesFilter) => void;
}> = ({ overview, isLoading, onOpen }) => (
  <div className="xgs-invoice-factoring__summary">
    <MetricSummaryTile
      iconPreset="fileBlue"
      colorVariant="blue"
      isLoading={isLoading}
      title="Tracked invoices"
      value={overview?.pipeline.trackedInvoices ?? 0}
      legendDescription={`Unique invoices first tracked in the selected range, worth ${formatCents(
        overview?.pipeline.trackedValueCents ?? 0
      )}. Apply opens these invoices.`}
      onApply={() => onOpen({})}
    />
    <MetricSummaryTile
      iconPreset="fileGreenCheck"
      colorVariant="green"
      isLoading={isLoading}
      title="Submission complete"
      value={overview ? formatRate(overview.pipeline.submissionComplete) : "—"}
      legendDescription="Tracked invoices with a Denim job and all documents sent. Apply opens these invoices."
      onApply={() => onOpen({ status: "DOCUMENTS_SENT" })}
    />
    <MetricSummaryTile
      iconPreset="exclamationRed"
      colorVariant="red"
      isLoading={isLoading}
      title="Needs attention"
      value={overview?.issues.total ?? 0}
      legendDescription={`Tracked invoices not sent completely whose latest attempt failed or was skipped, worth ${formatCents(
        overview?.issues.valueCents ?? 0
      )}. Apply opens these invoices.`}
      onApply={() => onOpen({ view: "needsAttention" })}
    />
    <MetricSummaryTile
      iconPreset="clockYellow"
      colorVariant="yellow"
      isLoading={isLoading}
      title="Document gaps"
      value={overview?.documentGaps.total ?? 0}
      legendDescription={`Tracked invoices with a Denim job whose documents are not sent yet, worth ${formatCents(
        overview?.documentGaps.valueCents ?? 0
      )}. Apply opens these invoices.`}
      onApply={() => onOpen({ view: "documentGaps" })}
    />
    <MetricSummaryTile
      iconPreset="calendarBlue"
      colorVariant="purple"
      isLoading={isLoading}
      title="Processing time (median / 95th)"
      value={
        overview
          ? `${formatDuration(overview.processingTime.p50Seconds)} / ${formatDuration(overview.processingTime.p95Seconds)}`
          : "—"
      }
      legendDescription={`Time from first tracked to submission complete, across ${pluralize(
        overview?.processingTime.sampleSize ?? 0,
        "completed invoice"
      )}.`}
    />
  </div>
);

const FactoringOverview: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { terminalPrefix } = useFactoringContext();

  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [range, setRange] = useState(() => {
    const defaults = getDefaultRange();
    return { start: initialParams.get("start") || defaults.start, end: initialParams.get("end") || defaults.end };
  });
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("count");
  const [payorMetric, setPayorMetric] = useState<TrendMetric>("value");
  const [granularity, setGranularity] = useState<AnalyticsGranularity>(() => {
    const value = initialParams.get("granularity") as AnalyticsGranularity;
    return GRANULARITY_LABELS[value] ? value : "day";
  });

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "invoice_factoring_overview" });
  }, []);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const q = new URLSearchParams({ start: range.start, end: range.end, granularity });
    history.replace({ pathname: location.pathname, search: `?${q.toString()}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, granularity]);

  const { data: overview, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.INVOICE_FACTORING_OVERVIEW, range.start, range.end, granularity],
    queryFn: () => invoiceFactoringService.getAnalyticsOverview({ ...toApiRange(range.start, range.end), granularity }),
    keepPreviousData: true,
    retry: false,
    ...FACTORING_QUERY_OPTIONS,
  });

  const invoicesUrl = (filter: InvoicesFilter) => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.view) q.set("view", filter.view);
    if (overview) {
      q.set("trackedFrom", overview.range.from);
      q.set("trackedTo", overview.range.to);
    }
    return `${terminalPrefix}${Routes.billing.invoiceFactoringInvoices}?${q.toString()}`;
  };

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.INVOICE_FACTORING_OVERVIEW}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.INVOICE_FACTORING_OVERVIEW}
          title="Invoice Factoring"
          mods={{ "full-width": true }}
        />
      }
    >
      <div className="xgs-invoice-factoring">
        <FactoringNav />

        <div className="xgs-invoice-factoring__filters">
          <div className="xgs-invoice-factoring__filter-field">
            <span className="xgs-invoice-factoring__filter-label">Invoices first tracked:</span>
            <XGSDateRangePickerWithPresets
              start={range.start}
              end={range.end}
              onApply={(start, end) => setRange(start && end ? { start, end } : getDefaultRange())}
              maxDate={new Date()}
            />
          </div>
          <div className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-select">
            <span className="xgs-invoice-factoring__filter-label">Trend by:</span>
            <XGSSelect
              value={granularityOptions.find((o) => o.value === granularity)}
              options={granularityOptions}
              onValueChange={(o) => setGranularity((o?.value as AnalyticsGranularity) || "day")}
              isClearable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
            />
          </div>
          <Button
            theme={ButtonThemes.blueInverted}
            size={ButtonSizes.auto}
            type="button"
            spinner={isFetching && !isLoading}
            onClick={() => refetch()}
            className="xgs-invoice-factoring__filter-clear"
          >
            Refresh
          </Button>
          {overview && <span className="xgs-invoice-factoring__muted">Updated {formatDateTime(overview.generatedAt)}</span>}
        </div>

        {isError && (
          <div className="xgs-invoice-factoring__error-box">Unable to load the overview. {(error as Error)?.message}</div>
        )}

        <OverviewTiles overview={overview} isLoading={isLoading} onOpen={(filter) => history.push(invoicesUrl(filter))} />

        {overview && (
          <div className="xgs-invoice-factoring__overview-grid">
            <OverviewCard
              wide
              title="Submission trend"
              description={`${
                trendMetric === "value" ? "Invoice value" : "Invoices"
              } first tracked and submissions completed per period. Periods are in UTC.`}
              actions={<MetricToggle value={trendMetric} onChange={setTrendMetric} />}
            >
              <TrendTotals overview={overview} metric={trendMetric} />
              <SubmissionTrendChart overview={overview} metric={trendMetric} />
            </OverviewCard>
            <OverviewCard
              wide
              title="Payor trends"
              description={`${
                payorMetric === "value" ? "Invoice value" : "Invoices"
              } first tracked per period for the largest payors by value, out of ${pluralize(
                overview.payors.payorCount,
                "payor"
              )} in the range.`}
              actions={<MetricToggle value={payorMetric} onChange={setPayorMetric} />}
            >
              <PayorTrendChart overview={overview} metric={payorMetric} />
              <div className="xgs-invoice-factoring__section-title">Top payors by value</div>
              <PayorsTable overview={overview} />
            </OverviewCard>
            <OverviewCard title="Submission pipeline" description="Current status of tracked invoices.">
              <PipelineTable overview={overview} linkForStatus={(status) => invoicesUrl({ status })} />
            </OverviewCard>
            <OverviewCard
              title="Needs attention"
              description="Tracked invoices not sent completely, by the reason their latest attempt stopped."
            >
              <IssuesTable overview={overview} />
            </OverviewCard>
            <OverviewCard
              title="Document gaps"
              description="Tracked invoices with a Denim job whose documents are not sent yet, by age."
            >
              <DocumentGapChart overview={overview} />
            </OverviewCard>
            <OverviewCard title="Batch monitor" description="Batches uploaded in the selected range and their rows.">
              <BatchMonitorTable overview={overview} />
            </OverviewCard>
            <OverviewCard title="Retry health">
              <RetryHealthTable overview={overview} />
            </OverviewCard>
            <OverviewCard title="Duplicate prevention">
              <DuplicatePreventionTable overview={overview} />
            </OverviewCard>
          </div>
        )}
      </div>
    </ContentContainer>
  );
};

export default FactoringOverview;
